import React, {
  useRef,
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useCallback,
} from 'react'
import { Form, Select, Spin } from 'antd'
import { SIDRender, SLowcodeList } from 'shj-components'
import {
  getSignTemplateDetailList,
  getSignTemplateList,
  goodsManagementGetPrdArgManageListByGoodsId,
} from '@/services/goods.ts'
import config from '../config/ThirdConfig'
import dayjs from 'dayjs'
import { apiData2Table } from '@/utils/data'
import { httpOrgList } from '@/services/homeService'
import debounce from 'lodash/debounce'

const ThirdContent = forwardRef((props: any, ref) => {
  const { detail, current, goodsId, copyId } = props

  const listRef = useRef()
  const formRef = useRef<any>()
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [budgetList, setBudgetList] = useState<any>([])
  const [chooseTemplate, setChooseTemplate] = useState<string>('')
  const [templateListLength, setTemplateListLength] = useState<number>(0)
  const [templateOptions, setTemplateOptions] = useState<{ label: string; value: string }[]>([])
  const PAGE_SIZE = 20

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value) {
        getTemplateList(value, 1)
      } else {
        getTemplateList('', 1)
      }
    }, 300),
    []
  )

  // 获取签约模版列表
  const getTemplateList = (value: string, pageId = 1) => {
    setLoading(true)
    getSignTemplateList({
      params: {
        pageId,
        pageSize: PAGE_SIZE,
      },
      data: {
        enable: true,
        searchFlag: true,
        nameLike: value,
      },
    })
      .then((res: any) => {
        const newOptions = res.list.map((item: any) => ({
          label: item.name,
          value: item.id,
        }))

        if (pageId === 1) {
          // 首次加载或刷新
          setTemplateOptions(newOptions)
        } else {
          // 加载更多
          setTemplateOptions(prev => [...prev, ...newOptions])
        }

        // 判断是否还有更多数据
        setHasMore(Number(res.count) > pageId * PAGE_SIZE)
        setPage(pageId)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  // 处理滚动加载更多
  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { target } = e
    const div = target as HTMLDivElement

    // 当滚动到底部时加载更多
    if (div.scrollTop + div.clientHeight >= div.scrollHeight - 20 && !loading && hasMore) {
      getTemplateList(page + 1)
    }
  }

  // 获取签约模版配置
  const fetchList = useCallback(
    (params: any) => {
      return new Promise((resolve, reject) => {
        // 如果未选择签约模版，则返回错误
        if (!chooseTemplate) {
          reject({ success: false })
          return
        }
        // 如果报价类型数组为空，则返回错误
        if (budgetList.length === 0) {
          reject({ success: false })
          return
        }
        try {
          getSignTemplateDetailList({
            params: {
              pageId: params.current,
              pageSize: params.pageSize,
            },
            data: {
              searchFlag: true,
              quoteTypeList: !params.query.quoteType
                ? budgetList.map((item: any) => item.code)
                : undefined, // 与商品相关的报价类型（查询条件存在时不传）
              signTemplateId: chooseTemplate, // 签约模版
              quoteType: params.query.quoteType, // 报价类型
              quotaContractId: params.query.quotaContractId, // 报价合同配置ID
              titleLike: params.query.title, // 标题
              orgId: params.query.orgId, // 适用门店
            },
          }).then((res: any) => {
            setTemplateListLength(res.list.length)
            resolve(apiData2Table(res))
          })
        } catch (error) {
          reject({ success: false })
        }
      })
    },
    [budgetList, chooseTemplate]
  )

  // 获取与商品相关的报价类型
  const getBudgetList = useCallback(async () => {
    const budgetRes: any = await goodsManagementGetPrdArgManageListByGoodsId({
      goodsId: goodsId || copyId,
    })
    const list = [...budgetRes, { code: 'bidProjectMCContract', name: '开工后缺断货变更' }]
    setBudgetList(list)
    listRef.current?.updateSearchData('budgetList', list)
  }, [goodsId, copyId])

  // 获取适用门店
  const getOrgList = async () => {
    // 适用门店
    const companyRes: any = await httpOrgList()
    const orgList = [{ name: '通用', id: '0' }, ...companyRes]
    listRef.current?.updateSearchData('orgList', orgList)
  }

  // 表格配置
  const extensions = {
    request: { fetchList },
    customRender: {
      // id
      idRender(text: string) {
        return <SIDRender id={text} />
      },
      // 开始时间
      startDateRender(_text: any, record: Record<string, any>) {
        return record.startDate ? dayjs(record.startDate).format('YYYY-MM-DD') : ''
      },
      // 结束时间
      endDateRender(_text: any, record: Record<string, any>) {
        return record.endDate ? dayjs(record.endDate).format('YYYY-MM-DD') : ''
      },
    },
  }

  useImperativeHandle(ref, () => ({
    getFormData: { chooseTemplate, templateListLength },
  }))

  useEffect(() => {
    // 判断当前组件是否展示
    if (current === 2) {
      getOrgList()
      getBudgetList()
      getTemplateList('', 1)
      // 编辑
      if (detail?.signTemplateId) {
        setChooseTemplate(detail.signTemplateId)
        formRef.current?.setFieldsValue({
          signTemplateId: detail?.signTemplateName || detail.signTemplateId,
        })
      }
    }
  }, [current, detail?.signTemplateId, detail?.signTemplateName, getBudgetList])

  // 监听签约模版选项变化
  useEffect(() => {
    listRef.current?.reload()
  }, [chooseTemplate])

  // 监听报价类型变化
  useEffect(() => {
    budgetList.length > 0 && listRef.current?.reload()
  }, [budgetList])

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Form ref={formRef}>
        <Form.Item
          name="signTemplateId"
          label="签约模板"
          rules={[{ required: true, message: '请选择签约模板' }]}
          style={{ width: 460 }}
        >
          <Select
            placeholder="请选择签约模板"
            style={{ width: '100%' }}
            options={templateOptions}
            onPopupScroll={handlePopupScroll}
            loading={loading}
            dropdownRender={menu => (
              <div>
                {menu}
                {loading && (
                  <div style={{ textAlign: 'center', padding: '8px 0' }}>
                    <Spin size="small" />
                  </div>
                )}
              </div>
            )}
            onChange={value => setChooseTemplate(value)}
            showSearch
            allowClear
            onSearch={debouncedSearch}
            onClear={() => {
              setChooseTemplate('')
              getTemplateList('', 1)
            }}
            filterOption={false}
            notFoundContent={loading ? <Spin size="small" /> : '无数据'}
          />
        </Form.Item>
      </Form>
      {/* 数据表格 */}
      <SLowcodeList ref={listRef} searchMode="group" config={config} {...extensions} />
    </div>
  )
})
export default ThirdContent
